import { FunctionComponent, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { providersApi, ApiError } from '../../../services/api';
import styles from './RateProviderModal.module.css';

interface RateProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId?: string;
  providerName: string;
  providerImage?: string;
  visitDate?: string;
  service?: string;
}

interface RatingCategory {
  id: string;
  label: string;
  value: number;
}

const RateProviderModal: FunctionComponent<RateProviderModalProps> = ({
  isOpen,
  onClose,
  providerId,
  providerName,
  providerImage,
  visitDate,
  service = 'Recent Visit'
}) => {
  const [overallRating, setOverallRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [categories, setCategories] = useState<RatingCategory[]>([
    { id: 'wait-time', label: 'Wait Time', value: 0 },
    { id: 'bedside-manner', label: 'Bedside Manner', value: 0 },
    { id: 'explanation', label: 'Explanation of Treatment', value: 0 },
    { id: 'staff', label: 'Office Staff', value: 0 },
    { id: 'facility', label: 'Office Environment', value: 0 }
  ]);
  const [recommendProvider, setRecommendProvider] = useState<boolean | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const predefinedTags = [
    'Professional',
    'Caring',
    'Thorough',
    'Good Listener',
    'Knowledgeable',
    'Efficient',
    'Friendly Staff',
    'Clean Office',
    'Easy Parking',
    'Minimal Wait',
    'Clear Communication',
    'Respectful'
  ];

  const handleOverallRatingClick = (rating: number) => {
    setOverallRating(rating);
  };

  const handleCategoryRating = (categoryId: string, rating: number) => {
    setCategories(prev => prev.map(cat => 
      cat.id === categoryId ? { ...cat, value: rating } : cat
    ));
  };

  const toggleTag = (tag: string) => {
    setTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const isFormValid = () => {
    return overallRating > 0 && recommendProvider !== null;
  };

  const handleSubmit = async () => {
    if (!isFormValid() || !providerId) return;

    setIsSubmitting(true);
    
    try {
      const reviewData = {
        overallRating,
        categoryRatings: categories.reduce((acc, cat) => ({
          ...acc,
          [cat.id]: cat.value
        }), {}),
        wouldRecommend: recommendProvider,
        comment: reviewText,
        tags,
        visitDate,
        service
      };

      await providersApi.rateProvider(providerId, reviewData);
      
      setShowSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error instanceof ApiError) {
        // Could add error state/message display here if needed
        console.error('API Error:', error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setOverallRating(0);
    setHoverRating(0);
    setCategories(prev => prev.map(cat => ({ ...cat, value: 0 })));
    setRecommendProvider(null);
    setReviewText('');
    setTags([]);
    setShowSuccess(false);
    onClose();
  };

  const renderStars = (rating: number, hovered: number, onClick?: (rating: number) => void, onHover?: (rating: number) => void) => {
    return (
      <div className={styles.starsContainer}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            className={styles.starButton}
            onClick={() => onClick?.(star)}
            onMouseEnter={() => onHover?.(star)}
            onMouseLeave={() => onHover?.(0)}
            type="button"
          >
            {star <= (hovered || rating) ? (
              <StarIcon className={styles.starFilled} />
            ) : (
              <StarBorderIcon className={styles.starEmpty} />
            )}
          </button>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={handleClose} />
      <div className={styles.modalContent}>
        {!showSuccess ? (
          <>
            {/* Header */}
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Rate Your Visit</h2>
              <button className={styles.closeButton} onClick={handleClose}>
                <CloseIcon />
              </button>
            </div>

            {/* Provider Info */}
            <div className={styles.providerInfo}>
              <img 
                src={providerImage || '/images/provider-placeholder.svg'} 
                alt={providerName}
                className={styles.providerImage}
              />
              <div className={styles.providerDetails}>
                <h3 className={styles.providerName}>{providerName}</h3>
                <p className={styles.visitInfo}>
                  {service} • {visitDate && new Date(visitDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Overall Rating */}
            <div className={styles.ratingSection}>
              <h3 className={styles.sectionTitle}>Overall Experience</h3>
              <div className={styles.overallRating}>
                {renderStars(overallRating, hoverRating, handleOverallRatingClick, setHoverRating)}
                <span className={styles.ratingLabel}>
                  {hoverRating || overallRating ? 
                    ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][Math.max(0, (hoverRating || overallRating) - 1)]
                    : 'Select a rating'
                  }
                </span>
              </div>
            </div>

            {/* Category Ratings */}
            {overallRating > 0 && (
              <div className={styles.categorySection}>
                <h3 className={styles.sectionTitle}>Rate Specific Aspects (Optional)</h3>
                <div className={styles.categories}>
                  {categories.map(category => (
                    <div key={category.id} className={styles.categoryRow}>
                      <span className={styles.categoryLabel}>{category.label}</span>
                      {renderStars(
                        category.value, 
                        0, 
                        (rating) => handleCategoryRating(category.id, rating)
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Would Recommend */}
            {overallRating > 0 && (
              <div className={styles.recommendSection}>
                <h3 className={styles.sectionTitle}>Would you recommend {providerName}?</h3>
                <div className={styles.recommendOptions}>
                  <button
                    className={`${styles.recommendButton} ${recommendProvider === true ? styles.selected : ''}`}
                    onClick={() => setRecommendProvider(true)}
                  >
                    <ThumbUpIcon />
                    <span>Yes, I would</span>
                  </button>
                  <button
                    className={`${styles.recommendButton} ${recommendProvider === false ? styles.selected : ''}`}
                    onClick={() => setRecommendProvider(false)}
                  >
                    <ThumbUpIcon style={{ transform: 'rotate(180deg)' }} />
                    <span>No, I wouldn't</span>
                  </button>
                </div>
              </div>
            )}

            {/* Tags */}
            {overallRating > 0 && (
              <div className={styles.tagsSection}>
                <h3 className={styles.sectionTitle}>What stood out? (Optional)</h3>
                <div className={styles.tags}>
                  {predefinedTags.map(tag => (
                    <button
                      key={tag}
                      className={`${styles.tag} ${tags.includes(tag) ? styles.selected : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Review Text */}
            {overallRating > 0 && (
              <div className={styles.reviewSection}>
                <h3 className={styles.sectionTitle}>Additional Comments (Optional)</h3>
                <textarea
                  className={styles.reviewTextarea}
                  placeholder="Share more details about your experience..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  rows={4}
                  maxLength={500}
                />
                <span className={styles.charCount}>{reviewText.length}/500</span>
              </div>
            )}

            {/* Privacy Notice */}
            <div className={styles.privacyNotice}>
              <p>Your review will be posted publicly with your first name and last initial only.</p>
            </div>

            {/* Footer */}
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={handleClose}>
                Cancel
              </button>
              <button 
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={!isFormValid() || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </>
        ) : (
          /* Success Screen */
          <div className={styles.successScreen}>
            <div className={styles.successIcon}>
              <CheckCircleIcon style={{ fontSize: 64, color: '#10b981' }} />
            </div>
            <h2 className={styles.successTitle}>Thank You!</h2>
            <p className={styles.successMessage}>
              Your review has been submitted successfully.
              It will appear on the provider's profile after a brief review.
            </p>
            <div className={styles.successStats}>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>{overallRating}</span>
                <span className={styles.statLabel}>Star Rating</span>
              </div>
              {tags.length > 0 && (
                <div className={styles.statItem}>
                  <span className={styles.statNumber}>{tags.length}</span>
                  <span className={styles.statLabel}>Qualities Noted</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RateProviderModal;